import { FC, PropsWithChildren, useEffect, useState } from 'react'
import { List, ListItem } from '@mui/material'

import CurbsNavSectionHeader from '@/components/layouts/CurbsNavSectionHeader'

interface CurbsNavCollapsibleSectionProps {
  drawerOpen: boolean
  onExpandDrawer: () => void
  icon: any
  title: string
  testId?: string
}

/**
 * A nav item that expands in place to reveal its children.
 *
 * Renders as a single list item so it can sit in the same list as the plain
 * nav items, with its children in a list nested inside it.
 *
 * Page groups do not use this: their sub-pages open in `CurbsNavFlyout`.
 */
const CurbsNavCollapsibleSection: FC<PropsWithChildren<CurbsNavCollapsibleSectionProps>> = ({
  drawerOpen,
  onExpandDrawer,
  icon,
  title,
  testId,
  children,
}) => {
  const [open, setOpen] = useState(false)

  // Collapsed, the section's contents have nowhere to render, so the first
  // click expands the drawer instead of toggling into an invisible section.
  const handleClick = () => {
    if (!drawerOpen) {
      onExpandDrawer()
      setOpen(true)
      return
    }
    setOpen(current => !current)
  }

  useEffect(() => {
    if (!drawerOpen) setOpen(false)
  }, [drawerOpen])

  return (
    <ListItem disablePadding sx={{ display: 'block' }}>
      <CurbsNavSectionHeader
        drawerOpen={drawerOpen}
        icon={icon}
        title={title}
        testId={testId}
        expanded={open}
        onClick={handleClick}
      />
      {open && <List disablePadding>{children}</List>}
    </ListItem>
  )
}

export default CurbsNavCollapsibleSection
